import { useState, useEffect } from 'react'
import {
  MdBookOnline,
  MdEdit,
  MdArrowBack,
  MdPerson,
  MdFlight,
  MdBusiness,
  MdAttachMoney,
  MdSchedule,
  MdCheckCircle,
  MdCancel,
  MdPayment,
  MdLocationOn,
  MdPhone,
  MdTimeline,
  MdCreditCard,
  MdReceipt,
} from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, LoadingSpinner, Badge, InfoCard } from '../../components/ui'
import { useBooking } from '../../hooks/useBooking'
import { useWebsocket } from '../../hooks/useWebsocket'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import { getWebsocketChannel } from '../../utils/websocket'

function BookingDetailsPage() {
  const dispatch = useDispatch()
  const { bookingId } = useParams()
  const navigate = useNavigate()
  const [booking, setBooking] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const { getBookingAsync, isGetBookingLoading } = useBooking()
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()

  const loadBooking = async () => {
    try {
      setIsLoading(true)
      const bookingData = await getBookingAsync(bookingId)
      console.log('#bookingData:', JSON.stringify(bookingData, null, 2))
      setBooking(bookingData)
    } catch (error) {
      console.error('Error loading booking:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (bookingId) {
      loadBooking()

      const channelName = getWebsocketChannel(bookingId).bookingDetails

      if (channelName) {
        subscribeToChannel(channelName)
        dispatch(addSubscription(channelName))
      }

      return () => {
        if (channelName) {
          unsubscribeFromChannel(channelName)
          dispatch(removeSubscription(channelName))
        }
      }
    }
  }, [bookingId]) // eslint-disable-line react-hooks/exhaustive-deps

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success'
      case 'pending':
        return 'warning'
      case 'cancelled':
        return 'error'
      case 'completed':
        return 'info'
      default:
        return 'neutral'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <MdCheckCircle className="text-success-600 dark:text-success-400" />
      case 'pending':
        return <MdSchedule className="text-warning-600 dark:text-warning-400" />
      case 'cancelled':
        return <MdCancel className="text-error-600 dark:text-error-400" />
      case 'completed':
        return <MdCheckCircle className="text-info-600 dark:text-info-400" />
      default:
        return <MdBookOnline className="text-neutral-600 dark:text-neutral-400" />
    }
  }

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success'
      case 'pending':
        return 'warning'
      case 'failed':
        return 'error'
      case 'refunded':
        return 'info'
      default:
        return 'neutral'
    }
  }

  const handleEditBooking = () => {
    navigate(`/bookings/${bookingId}/edit`)
  }

  const handleBackToBookings = () => {
    navigate(-1)
  }

  if (isLoading || isGetBookingLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!booking) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
            Booking Not Found
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            The booking you're looking for doesn't exist or has been removed.
          </p>
          <Button variant="primary" onClick={handleBackToBookings}>
            Back to Bookings
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="text-left mb-4">
          <Button
            variant="ghost"
            onClick={handleBackToBookings}
            className="flex items-center space-x-2"
          >
            <MdArrowBack className="text-lg" />
            <span>Back to Bookings</span>
          </Button>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="text-left">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
                  {booking.bookingNumber}
                </h1>
                <Badge variant={getStatusColor(booking.status)}>
                  <div className="flex items-center gap-1">
                    {getStatusIcon(booking.status)}
                    <span className="capitalize">{booking.status}</span>
                  </div>
                </Badge>
                <Badge variant={getPaymentStatusColor(booking.paymentStatus)}>
                  <div className="flex items-center gap-1">
                    <MdPayment className="text-sm" />
                    <span className="capitalize">{booking.paymentStatus}</span>
                  </div>
                </Badge>
              </div>
              <p className="text-neutral-600 dark:text-neutral-400">
                {booking.flight?.number} • {booking.passengers?.length || 0} passenger(s) •{' '}
                {formatCurrency(booking.price, booking.paymentCurrency)}
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            onClick={handleEditBooking}
            className="flex items-center space-x-2"
          >
            <MdEdit className="text-lg" />
            <span>Edit Booking</span>
          </Button>
        </div>
      </div>

      {/* Basic Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <InfoCard title="Booking Information" icon={MdBookOnline}>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Booking Number</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {booking.bookingNumber}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Status</span>
              <Badge variant={getStatusColor(booking.status)}>
                <span className="capitalize">{booking.status}</span>
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Price</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {formatCurrency(booking.price, booking.paymentCurrency)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Created</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {formatDate(booking.createdAt)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Updated</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {formatDate(booking.updatedAt)}
              </span>
            </div>
          </div>
        </InfoCard>

        <InfoCard title="Flight Information" icon={MdFlight}>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Flight Number</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {booking.flight?.number}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Status</span>
              <Badge variant="success">{booking.flight?.status}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Departure</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {booking.flight?.departure ? formatDateTime(booking.flight.departure) : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Arrival</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {booking.flight?.arrival ? formatDateTime(booking.flight.arrival) : 'N/A'}
              </span>
            </div>
            {booking.flight?._id && (
              <div className="pt-2 border-t border-neutral-200 dark:border-neutral-700">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/flights/${booking.flight._id}`)}
                  className="w-full flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                >
                  <MdFlight className="text-sm" />
                  View Flight Details
                </Button>
              </div>
            )}
          </div>
        </InfoCard>

        <InfoCard title="Payment Information" icon={MdPayment}>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Payment Status</span>
              <Badge variant={getPaymentStatusColor(booking.paymentStatus)}>
                <span className="capitalize">{booking.paymentStatus}</span>
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Payment Method</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {booking.paymentMethod?.replace('_', ' ')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Amount</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {formatCurrency(booking.paymentAmount || booking.price, booking.paymentCurrency)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Transaction ID</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {booking.paymentTransactionId || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600 dark:text-neutral-400">Payment Date</span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {booking.paymentDate ? formatDateTime(booking.paymentDate) : 'N/A'}
              </span>
            </div>
          </div>
        </InfoCard>
      </div>

      {/* Passengers Information */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdPerson className="text-primary-600 dark:text-primary-400" />
          Passengers ({booking.passengers?.length || 0})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {booking.passengers?.map((passengerBooking, index) => (
            <div
              key={passengerBooking._id || index}
              className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4"
            >
              <div className="flex items-center gap-2 mb-3">
                <MdPerson className="text-primary-600 dark:text-primary-400" />
                <span className="font-medium text-neutral-900 dark:text-white">
                  Passenger {index + 1}
                </span>
                <Badge variant="info">{passengerBooking.seat}</Badge>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Name</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {passengerBooking.passenger?.firstName} {passengerBooking.passenger?.lastName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Email</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {passengerBooking.passenger?.email}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Phone</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {passengerBooking.passenger?.phone}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600 dark:text-neutral-400">Passport</span>
                  <span className="font-medium text-neutral-900 dark:text-white">
                    {passengerBooking.passenger?.passport?.number}
                  </span>
                </div>
              </div>
              {passengerBooking.passenger?._id && (
                <div className="pt-3 border-t border-neutral-200 dark:border-neutral-700 mt-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/passengers/${passengerBooking.passenger._id}`)}
                    className="w-full flex items-center justify-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                  >
                    <MdPerson className="text-sm" />
                    View Passenger Details
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Airline Information */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdBusiness className="text-primary-600 dark:text-primary-400" />
          Airline Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdBusiness className="text-primary-600 dark:text-primary-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Airline Details
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <div className="font-medium text-neutral-900 dark:text-white">
                {booking.flight?.airline?.name}
              </div>
              <div className="text-neutral-600 dark:text-neutral-400">
                Code: {booking.flight?.airline?.code}
              </div>
              <div className="text-neutral-600 dark:text-neutral-400">
                Country: {booking.flight?.airline?.country}
              </div>
            </div>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdPhone className="text-info-600 dark:text-info-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Contact Information
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <div className="text-neutral-600 dark:text-neutral-400">
                Phone: {booking.flight?.airline?.phone}
              </div>
              <div className="text-neutral-600 dark:text-neutral-400">
                Founded:{' '}
                {booking.flight?.airline?.founded
                  ? formatDate(booking.flight.airline.founded)
                  : 'N/A'}
              </div>
            </div>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdLocationOn className="text-success-600 dark:text-success-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Headquarters
              </span>
            </div>
            <div className="space-y-1 text-sm">
              <div className="text-neutral-600 dark:text-neutral-400">
                City: {booking.flight?.airline?.headquarters?.city}
              </div>
              <div className="text-neutral-600 dark:text-neutral-400">
                Country: {booking.flight?.airline?.headquarters?.country}
              </div>
              <div className="text-neutral-600 dark:text-neutral-400">
                Address: {booking.flight?.airline?.headquarters?.address}
              </div>
            </div>
          </div>
        </div>
        {booking.flight?.airline?._id && (
          <div className="mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <Button
              variant="ghost"
              onClick={() => navigate(`/airlines/${booking.flight.airline._id}`)}
              className="flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              <MdBusiness className="text-sm" />
              View Airline Details
            </Button>
          </div>
        )}
      </div>

      {/* Payment Details */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdReceipt className="text-primary-600 dark:text-primary-400" />
          Payment Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MdAttachMoney className="text-success-600 dark:text-success-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Total Amount
              </span>
            </div>
            <p className="text-2xl font-bold text-neutral-900 dark:text-white">
              {formatCurrency(booking.paymentAmount || booking.price, booking.paymentCurrency)}
            </p>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MdCreditCard className="text-info-600 dark:text-info-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Payment Method
              </span>
            </div>
            <p className="text-lg font-bold text-neutral-900 dark:text-white">
              {booking.paymentMethod?.replace('_', ' ')}
            </p>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MdPayment className="text-warning-600 dark:text-warning-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Transaction Status
              </span>
            </div>
            <p className="text-lg font-bold text-neutral-900 dark:text-white">
              {booking.paymentTransactionStatus}
            </p>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <MdSchedule className="text-error-600 dark:text-error-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Payment Date
              </span>
            </div>
            <p className="text-sm font-bold text-neutral-900 dark:text-white">
              {booking.paymentDate ? formatDate(booking.paymentDate) : 'N/A'}
            </p>
          </div>
        </div>
        <div className="mt-6 p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <MdReceipt className="text-primary-600 dark:text-primary-400" />
            <span className="font-medium text-neutral-900 dark:text-white">
              Transaction Information
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-neutral-600 dark:text-neutral-400">Transaction ID: </span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {booking.paymentTransactionId || 'N/A'}
              </span>
            </div>
            <div>
              <span className="text-neutral-600 dark:text-neutral-400">Currency: </span>
              <span className="font-medium text-neutral-900 dark:text-white">
                {booking.paymentCurrency}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Information */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6 flex items-center gap-2">
          <MdTimeline className="text-primary-600 dark:text-primary-400" />
          Timeline Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdSchedule className="text-info-600 dark:text-info-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Booking Timeline
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Created</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {formatDateTime(booking.createdAt)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Last Updated</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {formatDateTime(booking.updatedAt)}
                </span>
              </div>
            </div>
          </div>
          <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <MdFlight className="text-success-600 dark:text-success-400" />
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                Flight Timeline
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Departure</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {booking.flight?.departure ? formatDateTime(booking.flight.departure) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Arrival</span>
                <span className="font-medium text-neutral-900 dark:text-white">
                  {booking.flight?.arrival ? formatDateTime(booking.flight.arrival) : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BookingDetailsPage
